/**
 * The setup wizard's price sentence (plan-017 WAPP-B, design §29.11 row 11, owner answer OQ-1: "show the relevant
 * rate"). The new VoiceAgent's template says which kinds of conversation it will have, so the sentence names the
 * organization's rates of those kinds, read through `useTalkRates` — never a literal:
 *
 * ```
 *   answers calls (inbound)   carrier_leg_inbound   (+ sip_trunk_inbound  when the organization has a SIP trunk)
 *   makes calls (a survey)    carrier_leg_outbound  (+ sip_trunk_outbound when the organization has a SIP trunk)
 * ```
 *
 * One figure while those kinds cost the same, else one "<kind> <rate> a minute" per kind (owner answer Q1). The
 * typical cost under it is at the highest of them, so "about" never understates. While the rates are not known yet
 * (or could not be read) the sentence names no figure and the typical cost is left out.
 *
 * AMENDED 2026-10-13 (plan-018 WAPP-A, R-15): an answering agent that takes no orders (the "Customer support" template)
 * is priced at the same inbound kinds, and its typical cost speaks of a call, not an order — decided by whether the
 * template has an order book, never by its code.
 */
import { useQuery } from "@tanstack/react-query";
import type { Money, TalkRateKind } from "@wetalk/domain";
import type { ReactNode } from "react";

import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
import { formatMoney, formatWholeMoney } from "../../../money.js";
import { useGateway } from "../../../api/provider.js";
import {
  oneTalkRateOf,
  talkRateKindLabel,
  talkRateText,
  useTalkRates,
  type TalkRateView,
} from "../../../api/slice/talk-rate.js";
import { sipTrunkListQuery } from "../../numbers/sip-trunk-query.js";

/** The kinds a new VoiceAgent of this direction is charged as (design §29.11 row 11). */
export function setupTalkRateKindsOf(outbound: boolean, hasSipTrunk: boolean): readonly TalkRateKind[] {
  if (outbound) return hasSipTrunk ? ["carrier_leg_outbound", "sip_trunk_outbound"] : ["carrier_leg_outbound"];
  return hasSipTrunk ? ["carrier_leg_inbound", "sip_trunk_inbound"] : ["carrier_leg_inbound"];
}

/** The price line: one figure while the kinds agree, else each kind with its rate. `null` with no rate to name. */
export function setupPriceLineOf(rates: readonly TalkRateView[]): string | null {
  const one = oneTalkRateOf(rates);
  if (one !== null) return portalText("operations", "wizard.price_per_minute", { v0: formatMoney(one.perMinute) });
  if (rates.length === 0) return null;
  const each = rates.map((rate) => `${talkRateKindLabel(rate.kind)} ${talkRateText(rate.perMinute, "minute")}`).join(" · ");
  return portalText("operations", "wizard.price_per_kind", { v0: each });
}

function highestOf(rates: readonly TalkRateView[]): Money | null {
  let high: Money | null = null;
  for (const rate of rates) if (high === null || rate.perMinute.amountMinor > high.amountMinor) high = rate.perMinute;
  return high;
}

function times(rate: Money, factor: number): Money {
  return { amountMinor: Math.round(rate.amountMinor * factor), currency: rate.currency };
}

/**
 * The typical cost, at the highest of the rates: a 3-minute survey and 200 of them; a 2½-minute order and 20 a day
 * for 30 days. `null` with no rate to price at.
 */
export function setupTypicalCostOf(rates: readonly TalkRateView[], outbound: boolean, hasOrderBook = true): string | null {
  const high = highestOf(rates);
  if (high === null) return null;
  if (outbound) {
    return portalText("operations", "wizard.survey_typical_cost", { v0: formatMoney(times(high, 3)), v1: formatWholeMoney(times(high, 600)) });
  }
  return hasOrderBook
    ? portalText("operations", "wizard.order_typical_cost", { v0: formatMoney(times(high, 2.5)), v1: formatWholeMoney(times(high, 1500)) })
    : portalText("core", "wizard.information.call_typical_cost", { v0: formatMoney(times(high, 2.5)), v1: formatWholeMoney(times(high, 1500)) });
}

export function SetupCost({ outbound, hasOrderBook = true }: { readonly outbound: boolean; readonly hasOrderBook?: boolean }): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const talkRate = useTalkRates();
  const trunk = useQuery(sipTrunkListQuery(gateway));
  const kinds = setupTalkRateKindsOf(outbound, (trunk.data?.length ?? 0) > 0);
  const rates = (talkRate.data?.talkRates ?? []).filter((one) => kinds.includes(one.kind));
  const line = setupPriceLineOf(rates);
  const typical = setupTypicalCostOf(rates, outbound, hasOrderBook);
  return (
    <div className="c-setup-cost">
      <span className="c-setup-cost-line">{line ?? portalText("operations", "wizard.price_at_your_rates")}</span>
      {typical === null ? null : <span className="c-setup-cost-est">{typical}</span>}
    </div>
  );
}
